import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Play, Square, Trash2, Pencil, ChevronLeft, ChevronRight, Send, Lock, CheckSquare, BarChart3, FolderKanban } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import { TIMER_EVENT, formatElapsed } from '../../utils/timeFormat';
import { field, ghost, primary, muted, card, hm, errorText, addDays, weekLabel } from './styles';
import { StatusBadge, Message, EntryDays } from './ui';

const pad = (n) => String(n).padStart(2, '0');
const toLocalDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalInput = (iso) => { const d = new Date(iso); return `${toLocalDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const mondayOf = (key) => { const d = new Date(`${key}T00:00:00Z`); return addDays(key, -((d.getUTCDay() + 6) % 7)); };

const Tracker = () => {
    const [params, setParams] = useSearchParams();
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = ['ADMIN', 'HR'].includes(me.role);
    const today = toLocalDate(new Date());
    const currentWeek = mondayOf(today);
    const week = /^\d{4}-\d{2}-\d{2}$/.test(params.get('week') || '') ? mondayOf(params.get('week')) : currentWeek;

    const [settings, setSettings] = useState({ timeEntryMode: 'BOTH' });
    const [projects, setProjects] = useState([]);
    const [sheet, setSheet] = useState(null);
    const [running, setRunning] = useState(null);
    const [hasTeam, setHasTeam] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const [message, setMessage] = useState(null);
    const [timerForm, setTimerForm] = useState({ projectId: '', taskId: '', description: '', isBillable: true });
    // Manual entries default to a day inside the week on screen.
    const [manual, setManual] = useState({ projectId: '', taskId: '', description: '', date: week === currentWeek ? today : week, start: '09:00', end: '10:00', isBillable: true });
    const [editing, setEditing] = useState(null);

    const timerOn = settings.timeEntryMode !== 'MANUAL';
    const manualOn = settings.timeEntryMode !== 'TIMER';
    const say = (ok, text) => setMessage({ ok, text });
    const managesAProject = isAdmin || projects.some(p => p.managerId === me.id);
    const locked = sheet && (sheet.status === 'SUBMITTED' || sheet.status === 'APPROVED');

    const load = useCallback(async () => {
        try {
            const [s, p, w, c, t] = await Promise.all([
                api.get('/time/settings'), api.get('/time/projects'), api.get(`/time/timesheets/me?week=${week}`),
                api.get('/time/timer/current'), api.get(`/time/timesheets/team?week=${week}`),
            ]);
            setSettings(s.data); setProjects(p.data); setSheet(w.data); setRunning(c.data);
            setHasTeam(t.data.people.length > 0);
        } catch (e) {
            say(false, errorText(e, 'Failed to load time tracking'));
        }
    }, [week]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [running]);

    const goToWeek = (w) => {
        setMessage(null);
        setParams(w === currentWeek ? {} : { week: w });
        setManual(m => ({ ...m, date: w === currentWeek ? today : w }));
    };

    // Projects this person can actually log to (members or open projects; never templates).
    const loggable = projects.filter(p => p.isActive && !p.isTemplate && (p.isPublic || p.members.some(m => m.id === me.id)));
    const tasksOf = (id) => (projects.find(p => p.id === id)?.tasks || []).filter(t => t.isActive);

    const act = async (fn, okText) => {
        try { await fn(); if (okText) say(true, okText); await load(); window.dispatchEvent(new Event(TIMER_EVENT)); }
        catch (e) { say(false, errorText(e, 'Something went wrong')); }
    };

    const start = () => act(() => api.post('/time/timer/start', {
        projectId: timerForm.projectId, taskId: timerForm.taskId || undefined,
        description: timerForm.description, isBillable: timerForm.isBillable,
    }));
    const stop = () => act(() => api.post('/time/timer/stop'), 'Timer stopped');
    const submit = () => window.confirm(`Submit your timesheet for ${weekLabel(week)}? You won't be able to change it unless it is returned to you.`)
        && act(() => api.post('/time/timesheets/submit', { week }), 'Timesheet submitted for approval');

    const addManual = (e) => {
        e.preventDefault();
        const entryWeek = mondayOf(manual.date);
        act(async () => {
            await api.post('/time/entries', {
                projectId: manual.projectId, taskId: manual.taskId || undefined, description: manual.description,
                isBillable: manual.isBillable,
                startedAt: new Date(`${manual.date}T${manual.start}`).toISOString(),
                endedAt: new Date(`${manual.date}T${manual.end}`).toISOString(),
            });
            // Show the week the entry went into, so it never "disappears".
            if (entryWeek !== week) setParams(entryWeek === currentWeek ? {} : { week: entryWeek });
        }, 'Entry added');
    };

    const saveEdit = (e) => {
        e.preventDefault();
        const body = { description: editing.description, isBillable: editing.isBillable };
        // Only send project/task when changed, so people who have left a project can still fix a description.
        if (editing.projectId !== editing.orig.projectId || editing.taskId !== editing.orig.taskId) {
            body.projectId = editing.projectId; body.taskId = editing.taskId || null;
        }
        if (manualOn || isAdmin) {
            body.startedAt = new Date(editing.startedAt).toISOString();
            body.endedAt = new Date(editing.endedAt).toISOString();
        }
        act(async () => { await api.patch(`/time/entries/${editing.id}`, body); setEditing(null); }, 'Entry updated');
    };

    const remove = (entry) => window.confirm('Delete this entry?') && act(() => api.delete(`/time/entries/${entry.id}`), 'Entry deleted');

    const projectSelect = (value, onChange, label) => (
        <select aria-label={label} style={field} value={value} onChange={e => onChange(e.target.value)}>
            <option value="">Select project</option>
            {loggable.map(p => <option key={p.id} value={p.id}>{p.name}{p.client ? ` (${p.client.name})` : ''}</option>)}
        </select>
    );

    const finished = sheet ? sheet.entries.filter(e => e.endedAt) : [];
    const canSubmit = sheet && !locked && !sheet.isFuture && sheet.totalSec > 0;

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 980 }}>
            <PageHeader crumbs={crumbsFor('timesheet')} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, flex: 1 }}>Time Tracker</h1>
                {(isAdmin || hasTeam) && <Link to="/time/approvals" style={{ ...ghost, textDecoration: 'none' }}><CheckSquare size={14} /> Approvals</Link>}
                <Link to="/time/reports" style={{ ...ghost, textDecoration: 'none' }}><BarChart3 size={14} /> Reports</Link>
                {managesAProject && <Link to="/admin/time" style={{ ...ghost, textDecoration: 'none' }}><FolderKanban size={14} /> {isAdmin ? 'Setup' : 'My projects'}</Link>}
            </div>

            <Message message={message} />

            {loggable.length === 0 && !running && (
                <p style={muted}>You are not on any project yet. Ask your project manager or HR to add you.</p>
            )}

            {timerOn && (
                <section className="card" style={{ ...card, padding: 16, margin: '16px 0' }}>
                    {running ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 28, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatElapsed(now - new Date(running.startedAt).getTime())}</div>
                            <div>
                                <strong>{running.project?.name}</strong>{running.task && ` · ${running.task.name}`}
                                <div style={muted}>{running.description || 'No description'}</div>
                            </div>
                            <span style={{ flex: 1 }} />
                            <button className="btn btn-primary" style={primary} onClick={stop}><Square size={14} /> Stop</button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input aria-label="What are you working on?" style={{ ...field, flex: '1 1 220px' }} placeholder="What are you working on?" value={timerForm.description} onChange={e => setTimerForm({ ...timerForm, description: e.target.value })} />
                            {projectSelect(timerForm.projectId, (v) => setTimerForm({ ...timerForm, projectId: v, taskId: '', isBillable: projects.find(p => p.id === v)?.isBillable ?? true }), 'Timer project')}
                            <select aria-label="Timer task" style={field} value={timerForm.taskId} onChange={e => setTimerForm({ ...timerForm, taskId: e.target.value })}>
                                <option value="">No task</option>
                                {tasksOf(timerForm.projectId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                            <label><input type="checkbox" checked={timerForm.isBillable} onChange={e => setTimerForm({ ...timerForm, isBillable: e.target.checked })} /> Billable</label>
                            <button className="btn btn-primary" style={primary} onClick={start} disabled={!timerForm.projectId}><Play size={14} /> Start</button>
                        </div>
                    )}
                </section>
            )}

            {manualOn && (
                <section className="card" style={{ ...card, padding: 16, margin: '16px 0' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Add time manually</div>
                    <form onSubmit={addManual} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input aria-label="Description" style={{ ...field, flex: '1 1 200px' }} placeholder="Description" value={manual.description} onChange={e => setManual({ ...manual, description: e.target.value })} />
                        {projectSelect(manual.projectId, (v) => setManual({ ...manual, projectId: v, taskId: '', isBillable: projects.find(p => p.id === v)?.isBillable ?? true }), 'Entry project')}
                        <select aria-label="Entry task" style={field} value={manual.taskId} onChange={e => setManual({ ...manual, taskId: e.target.value })}>
                            <option value="">No task</option>
                            {tasksOf(manual.projectId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        <input aria-label="Date" type="date" max={today} style={field} value={manual.date} onChange={e => setManual({ ...manual, date: e.target.value })} />
                        <input aria-label="Start time" type="time" style={field} value={manual.start} onChange={e => setManual({ ...manual, start: e.target.value })} />
                        <input aria-label="End time" type="time" style={field} value={manual.end} onChange={e => setManual({ ...manual, end: e.target.value })} />
                        <label><input type="checkbox" checked={manual.isBillable} onChange={e => setManual({ ...manual, isBillable: e.target.checked })} /> Billable</label>
                        <button className="btn btn-primary" style={primary} type="submit" disabled={!manual.projectId}>Add entry</button>
                    </form>
                </section>
            )}

            {/* Week: navigation, status, totals, submit */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '24px 0 12px' }}>
                <button aria-label="Previous week" style={ghost} onClick={() => goToWeek(addDays(week, -7))}><ChevronLeft size={14} /></button>
                <h3 style={{ margin: 0 }} data-testid="week-label">{week === currentWeek ? 'This week' : weekLabel(week)}</h3>
                <button aria-label="Next week" style={ghost} onClick={() => goToWeek(addDays(week, 7))} disabled={week >= currentWeek}><ChevronRight size={14} /></button>
                {week !== currentWeek && <button style={{ ...ghost, border: 'none' }} onClick={() => goToWeek(currentWeek)}>Back to this week</button>}
                {sheet && <StatusBadge status={sheet.status} />}
                <span style={{ flex: 1 }} />
                {sheet && <span style={muted}>Billable {hm(sheet.billableSec)}</span>}
                {sheet && <strong style={{ fontSize: 18 }}>{hm(sheet.totalSec)}</strong>}
                {canSubmit && <button className="btn btn-primary" style={primary} onClick={submit}><Send size={14} /> Submit week</button>}
            </div>

            {sheet?.status === 'REJECTED' && sheet.rejectReason && (
                <div style={{ padding: '10px 14px', borderRadius: 6, marginBottom: 12, background: 'rgba(239,68,68,0.12)', color: 'var(--text-main)', border: '1px solid rgba(239,68,68,0.3)' }}>
                    <strong>Returned by {sheet.decidedBy?.name || 'your approver'}:</strong> {sheet.rejectReason}. Make your changes and submit again.
                </div>
            )}
            {locked && (
                <div style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <Lock size={13} /> {sheet.status === 'APPROVED' ? `Approved by ${sheet.decidedBy?.name || 'your approver'}; this week is locked.` : 'Submitted and waiting for approval; this week is locked until it is approved or returned.'}
                </div>
            )}

            {finished.length === 0 && <p style={muted}>No time logged in this week.</p>}
            <EntryDays entries={finished} actions={locked ? null : (e) => (
                <>
                    <button aria-label="Edit entry" style={ghost} onClick={() => setEditing({ id: e.id, description: e.description, isBillable: e.isBillable, projectId: e.projectId, taskId: e.taskId || '', orig: { projectId: e.projectId, taskId: e.taskId || '' }, startedAt: toLocalInput(e.startedAt), endedAt: toLocalInput(e.endedAt) })}><Pencil size={14} /></button>
                    <button aria-label="Delete entry" style={ghost} onClick={() => remove(e)}><Trash2 size={14} /></button>
                </>
            )} />

            {editing && (
                <div role="dialog" aria-label="Edit entry" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <form onSubmit={saveEdit} className="card" style={{ padding: 20, width: 440, maxWidth: '92vw', display: 'grid', gap: 10, background: 'var(--surface)' }}>
                        <h3 style={{ margin: 0 }}>Edit entry</h3>
                        <input aria-label="Edit description" style={field} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
                        <select aria-label="Edit project" style={field} value={editing.projectId} onChange={e => setEditing({ ...editing, projectId: e.target.value, taskId: '' })}>
                            {(isAdmin ? projects.filter(p => !p.isTemplate) : loggable).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <select aria-label="Edit task" style={field} value={editing.taskId} onChange={e => setEditing({ ...editing, taskId: e.target.value })}>
                            <option value="">No task</option>
                            {tasksOf(editing.projectId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        {(manualOn || isAdmin) && (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input aria-label="Edit start" type="datetime-local" style={{ ...field, flex: 1 }} value={editing.startedAt} onChange={e => setEditing({ ...editing, startedAt: e.target.value })} />
                                <input aria-label="Edit end" type="datetime-local" style={{ ...field, flex: 1 }} value={editing.endedAt} onChange={e => setEditing({ ...editing, endedAt: e.target.value })} />
                            </div>
                        )}
                        <label><input type="checkbox" checked={editing.isBillable} onChange={e => setEditing({ ...editing, isBillable: e.target.checked })} /> Billable</label>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button type="button" style={ghost} onClick={() => setEditing(null)}>Cancel</button>
                            <button type="submit" className="btn btn-primary" style={primary}>Save</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Tracker;
